import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ReferencePickerDialog from "../components/ReferencePickerDialog";
import { listUsers } from "../api/users";
import { listCardsPaged } from "../api/cards";
import { listSalesOrdersPaged } from "../api/salesOrders";
import { listProductionOrders } from "../api/productionOrders";
import {
  addTaskNote,
  cancelTask,
  completeTask,
  createTask,
  getTaskActivities,
  listTasks,
  reopenTask,
  updateTask,
} from "../api/tasks";

const TYPE_LABELS = {
  sample: "Numune",
  shipment: "Sevk",
  customer_followup: "Müşteri Dönüşü",
  purchase: "Satın Alma",
  maintenance: "Bakım",
  general: "Genel",
};
const PRIORITY_LABELS = { low: "Düşük", normal: "Normal", urgent: "Acil" };
const PRIORITY_COLORS = { low: "default", normal: "primary", urgent: "error" };
const STATUS_LABELS = {
  open: "Açık",
  completed: "Tamamlandı",
  cancelled: "İptal",
};

function localDateString(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function taskDate(task) {
  return task?.due_at ? String(task.due_at).slice(0, 10) : null;
}
function toDateTimeInput(value) {
  return value ? String(value).slice(0, 16).replace(" ", "T") : "";
}
function formatDateTime(value) {
  if (!value) return "Termin yok";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime())
    ? String(value).slice(0, 16)
    : new Intl.DateTimeFormat("tr-TR", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
}
function currentUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}
function emptyForm() {
  return {
    title: "",
    description: "",
    task_type: "general",
    priority: "normal",
    due_at: "",
    assigned_to_user_id: "",
    customer_card_id: "",
    sales_order_id: "",
    production_order_id: "",
  };
}

function TaskEditor({ open, task, users, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [picker, setPicker] = useState(null);
  const [referenceLabels, setReferenceLabels] = useState({
    customer: "",
    salesOrder: "",
    productionOrder: "",
  });
  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(
      task
        ? {
            title: task.title || "",
            description: task.description || "",
            task_type: task.task_type || "general",
            priority: task.priority || "normal",
            due_at: toDateTimeInput(task.due_at),
            assigned_to_user_id: task.assigned_to_user_id || "",
            customer_card_id: task.customer_card_id || "",
            sales_order_id: task.sales_order_id || "",
            production_order_id: task.production_order_id || "",
          }
        : emptyForm(),
    );
    setReferenceLabels(
      task
        ? {
            customer: task.customer_name || "",
            salesOrder: task.sales_order_no || "",
            productionOrder: task.production_order_no || "",
          }
        : { customer: "", salesOrder: "", productionOrder: "" },
    );
    setPicker(null);
  }, [open, task]);
  const change = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  const loadCustomers = useCallback(
    ({ page, search }) =>
      listCardsPaged({ type: "customer", search, page, limit: 10 }),
    [],
  );
  const loadOrders = useCallback(
    ({ page, search }) => listSalesOrdersPaged({ search, page, limit: 10 }),
    [],
  );
  const loadProductionOrders = useCallback(
    ({ page, search }) => listProductionOrders({ search, page, limit: 10 }),
    [],
  );
  const pickerConfig =
    picker === "customer"
      ? {
          title: "Müşteri Seç",
          searchPlaceholder: "Müşteri adı ile ara...",
          fetchPage: loadCustomers,
          getPrimary: (item) => item.name,
          getSecondary: (item) => item.code || "Müşteri kartı",
        }
      : picker === "salesOrder"
        ? {
            title: "Sipariş Seç",
            searchPlaceholder: "Sipariş no veya müşteri adı ile ara...",
            fetchPage: loadOrders,
            getPrimary: (item) => item.order_no,
            getSecondary: (item) => item.customer_name || "Sipariş",
          }
        : picker === "productionOrder"
          ? {
              title: "İş Emri Seç",
              searchPlaceholder: "İş emri no veya ürün adı ile ara...",
              fetchPage: loadProductionOrders,
              getPrimary: (item) => item.order_no,
              getSecondary: (item) =>
                item.product_name || item.customer_name || "İş emri",
            }
          : null;
  const selectReference = (item) => {
    if (picker === "customer") {
      setForm((prev) => ({ ...prev, customer_card_id: item.id }));
      setReferenceLabels((prev) => ({ ...prev, customer: item.name }));
    }
    if (picker === "salesOrder") {
      setForm((prev) => ({ ...prev, sales_order_id: item.id }));
      setReferenceLabels((prev) => ({ ...prev, salesOrder: item.order_no }));
    }
    if (picker === "productionOrder") {
      setForm((prev) => ({ ...prev, production_order_id: item.id }));
      setReferenceLabels((prev) => ({
        ...prev,
        productionOrder: item.order_no,
      }));
    }
    setPicker(null);
  };
  async function save() {
    if (!form.title.trim() || !form.assigned_to_user_id) {
      setError("Görev başlığı ve sorumlu kullanıcı zorunludur.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        assigned_to_user_id: Number(form.assigned_to_user_id),
        customer_card_id: form.customer_card_id
          ? Number(form.customer_card_id)
          : null,
        sales_order_id: form.sales_order_id
          ? Number(form.sales_order_id)
          : null,
        production_order_id: form.production_order_id
          ? Number(form.production_order_id)
          : null,
        due_at: form.due_at || null,
      };
      const saved = task
        ? await updateTask(task.id, payload)
        : await createTask(payload);
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Görev kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }
  const fieldSx = {
    "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2.25 },
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: { xs: 2.5, sm: 3.5 },
              py: 2.5,
              color: "#fff",
              background:
                "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2.25,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(255,255,255,0.16)",
                  border: "1px solid rgba(255,255,255,0.24)",
                  fontSize: 21,
                }}
              >
                ✓
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  {task ? "Görevi Düzenle" : "Yeni Görev Oluştur"}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Sorumluyu, termin tarihini ve iş bağlantılarını tek adımda
                  belirleyin.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc" }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              border: "1px solid #e7eaf3",
              mb: 2,
              bgcolor: "#fff",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography fontWeight={800}>Görev bilgileri</Typography>
              <Typography variant="body2" color="text.secondary">
                Bu alanlar görevin ne olduğunu, kimde olduğunu ve ne zaman
                yapılacağını tanımlar.
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <TextField
                  sx={fieldSx}
                  label="Görev başlığı"
                  placeholder="Örn. AMSCAN'a numune gönder"
                  value={form.title}
                  onChange={change("title")}
                  fullWidth
                  required
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth required sx={fieldSx}>
                  <InputLabel>Sorumlu kullanıcı</InputLabel>
                  <Select
                    label="Sorumlu kullanıcı"
                    value={form.assigned_to_user_id}
                    onChange={change("assigned_to_user_id")}
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth sx={fieldSx}>
                  <InputLabel>Görev türü</InputLabel>
                  <Select
                    label="Görev türü"
                    value={form.task_type}
                    onChange={change("task_type")}
                  >
                    {Object.entries(TYPE_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth sx={fieldSx}>
                  <InputLabel>Öncelik</InputLabel>
                  <Select
                    label="Öncelik"
                    value={form.priority}
                    onChange={change("priority")}
                  >
                    {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  sx={fieldSx}
                  label="Termin"
                  type="datetime-local"
                  value={form.due_at}
                  onChange={change("due_at")}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  sx={fieldSx}
                  label="Açıklama / yapılacak iş detayı"
                  placeholder="Görevin kapsamını veya dikkat edilmesi gereken bilgileri yazın."
                  value={form.description}
                  onChange={change("description")}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>
            </Grid>
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              border: "1px solid #e7eaf3",
              bgcolor: "#fff",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#6366f1",
                  }}
                />
                <Typography fontWeight={800}>İş bağlantıları</Typography>
                <Chip
                  label="İsteğe bağlı"
                  size="small"
                  variant="outlined"
                  sx={{ height: 22 }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Görevi müşteri, satış siparişi veya iş emriyle ilişkilendirerek
                bağlamını koruyun.
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {[
                {
                  key: "customer",
                  title: "Müşteri",
                  value: form.customer_card_id,
                  label: referenceLabels.customer,
                },
                {
                  key: "salesOrder",
                  title: "Sipariş",
                  value: form.sales_order_id,
                  label: referenceLabels.salesOrder,
                },
                {
                  key: "productionOrder",
                  title: "İş emri",
                  value: form.production_order_id,
                  label: referenceLabels.productionOrder,
                },
              ].map((reference) => (
                <Grid item xs={12} md={4} key={reference.key}>
                  <Paper
                    variant="outlined"
                    sx={{
                      minHeight: 74,
                      px: 1.5,
                      py: 1.1,
                      borderRadius: 2.25,
                      borderColor: reference.value ? "#c7d2fe" : "#dbe1ec",
                      bgcolor: reference.value ? "#f5f7ff" : "#fff",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {reference.title}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={reference.value ? 700 : 400}
                        noWrap
                        sx={{
                          minWidth: 0,
                          color: reference.value ? "#3730a3" : "text.secondary",
                        }}
                      >
                        {reference.label || "Bağlantı yok"}
                      </Typography>
                      <Stack direction="row" spacing={0.25}>
                        <Button
                          size="small"
                          onClick={() => setPicker(reference.key)}
                        >
                          {reference.value ? "Değiştir" : "Seç"}
                        </Button>
                        {reference.value ? (
                          <Button
                            size="small"
                            color="inherit"
                            onClick={() => {
                              const field =
                                reference.key === "customer"
                                  ? "customer_card_id"
                                  : reference.key === "salesOrder"
                                    ? "sales_order_id"
                                    : "production_order_id";
                              setForm((prev) => ({ ...prev, [field]: "" }));
                              setReferenceLabels((prev) => ({
                                ...prev,
                                [reference.key]: "",
                              }));
                            }}
                          >
                            ×
                          </Button>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3.5 },
            py: 2,
            bgcolor: "#fff",
            borderTop: "1px solid #e7eaf3",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            * Başlık ve sorumlu kullanıcı zorunludur.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={onClose}
              disabled={saving}
              sx={{ color: "text.secondary" }}
            >
              Vazgeç
            </Button>
            <Button
              variant="contained"
              onClick={save}
              disabled={saving}
              sx={{
                px: 2.5,
                borderRadius: 2,
                boxShadow: "0 8px 18px rgba(79, 70, 229, 0.24)",
              }}
            >
              {saving
                ? "Kaydediliyor..."
                : task
                  ? "Değişiklikleri Kaydet"
                  : "Görevi Kaydet"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      {pickerConfig ? (
        <ReferencePickerDialog
          open={Boolean(picker)}
          {...pickerConfig}
          onClose={() => setPicker(null)}
          onSelect={selectReference}
        />
      ) : null}
    </>
  );
}

function TaskDetails({ task, isAdmin, onClose, onChanged, onEdit }) {
  const [activities, setActivities] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const loadActivities = useCallback(async () => {
    if (!task) return;
    setLoading(true);
    try {
      setActivities(await getTaskActivities(task.id));
    } catch {
      setError("Görev geçmişi yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [task]);
  useEffect(() => {
    loadActivities();
    setNote("");
    setError("");
  }, [loadActivities]);
  if (!task) return null;
  async function complete() {
    try {
      await completeTask(task.id);
      await onChanged();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Görev tamamlanamadı.");
    }
  }
  async function reopen() {
    try {
      await reopenTask(task.id);
      await onChanged();
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.error?.message || "Görev yeniden açılamadı.",
      );
    }
  }
  async function cancel() {
    const reason = window.prompt("İptal nedeni (isteğe bağlı):");
    if (reason === null) return;
    try {
      await cancelTask(task.id, reason);
      await onChanged();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Görev iptal edilemedi.");
    }
  }
  async function saveNote() {
    if (!note.trim()) return;
    try {
      await addTaskNote(task.id, note);
      setNote("");
      await loadActivities();
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Not eklenemedi.");
    }
  }
  return (
    <Dialog open={Boolean(task)} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)" } }}>
      <DialogTitle sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)" }}><Typography variant="h6" fontWeight={800}>{task.title}</Typography><Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>Görev ayrıntılarını, hareket geçmişini ve işlem durumunu takip edin.</Typography></DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc", borderColor: "#e0e7ff", "& .MuiOutlinedInput-root": { borderRadius: 2.25, bgcolor: "#fff" } }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip label={TYPE_LABELS[task.task_type]} size="small" />
          <Chip
            label={PRIORITY_LABELS[task.priority]}
            color={PRIORITY_COLORS[task.priority]}
            size="small"
          />
          <Chip
            label={STATUS_LABELS[task.status]}
            variant="outlined"
            size="small"
          />
        </Stack>
        {task.description ? (
          <Typography sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
            {task.description}
          </Typography>
        ) : null}
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe" }}>
          <Typography variant="body2">
            <b>Sorumlu:</b> {task.assigned_to_name}
          </Typography>
          <Typography variant="body2">
            <b>Termin:</b> {formatDateTime(task.due_at)}
          </Typography>
          {task.customer_name ? (
            <Typography variant="body2">
              <b>Müşteri:</b> {task.customer_name}
            </Typography>
          ) : null}
          {task.sales_order_no ? (
            <Typography variant="body2">
              <b>Sipariş:</b> {task.sales_order_no}
            </Typography>
          ) : null}
          {task.production_order_no ? (
            <Typography variant="body2">
              <b>İş emri:</b> {task.production_order_no}
            </Typography>
          ) : null}
          {task.completed_at ? (
            <Typography variant="body2">
              <b>Tamamlayan:</b> {task.completed_by_name} —{" "}
              {formatDateTime(task.completed_at)}
            </Typography>
          ) : null}
          {task.cancelled_at ? (
            <Typography variant="body2">
              <b>İptal:</b> {task.cancelled_by_name} —{" "}
              {task.cancel_reason || "Neden belirtilmedi"}
            </Typography>
          ) : null}
        </Paper>
        <Typography variant="subtitle2" fontWeight={800}>
          Hareket geçmişi
        </Typography>
        {loading ? (
          <CircularProgress size={20} sx={{ m: 1 }} />
        ) : (
          <List dense disablePadding>
            {activities.map((item) => (
              <ListItem key={item.id} disableGutters>
                <ListItemText
                  primary={item.note || item.action}
                  secondary={`${item.actor_name || "Sistem"} · ${formatDateTime(item.created_at)}`}
                />
              </ListItem>
            ))}
          </List>
        )}
        <Divider sx={{ my: 1 }} />
        <Stack direction="row" spacing={1}>
          <TextField
            label="Not ekle"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            size="small"
            fullWidth
          />
          <Button onClick={saveNote} disabled={!note.trim()}>
            Ekle
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3.5 }, py: 2, bgcolor: "#fff", borderTop: "1px solid #e0e7ff", "& .MuiButton": { borderRadius: 2, textTransform: "none", fontWeight: 800 }, "& .MuiButton-contained:not(.Mui-disabled)": { bgcolor: "#4f46e5", "&:hover": { bgcolor: "#3730a3" } } }}>
        {isAdmin && task.status === "open" ? (
          <>
            <Button color="error" onClick={cancel}>
              İptal Et
            </Button>
            <Button onClick={() => onEdit(task)}>Düzenle</Button>
          </>
        ) : null}
        {isAdmin && task.status === "completed" ? (
          <Button onClick={reopen}>Yeniden Aç</Button>
        ) : null}
        {task.status === "open" ? (
          <Button variant="contained" color="success" onClick={complete}>
            Tamamlandı
          </Button>
        ) : null}
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
}

function MonthCalendar({
  month,
  tasks,
  selectedDate,
  onMonthChange,
  onDateSelect,
}) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = new Date(first);
  gridStart.setDate(1 - ((first.getDay() + 6) % 7));
  const taskByDate = useMemo(
    () =>
      tasks.reduce((map, task) => {
        const date = taskDate(task);
        if (date) (map[date] ||= []).push(task);
        return map;
      }, {}),
    [tasks],
  );
  const days = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    return day;
  });
  const label = new Intl.DateTimeFormat("tr-TR", {
    month: "long",
    year: "numeric",
  }).format(month);
  return (
    <Paper
      elevation={0}
      sx={{ p: 1.5, borderRadius: 4, height: "fit-content", bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.10)" }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Button
          size="small"
          onClick={() =>
            onMonthChange(
              new Date(month.getFullYear(), month.getMonth() - 1, 1),
            )
          }
        >
          ‹
        </Button>
        <Typography fontWeight={800} sx={{ textTransform: "capitalize" }}>
          {label}
        </Typography>
        <Button
          size="small"
          onClick={() =>
            onMonthChange(
              new Date(month.getFullYear(), month.getMonth() + 1, 1),
            )
          }
        >
          ›
        </Button>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
          mb: 0.5,
        }}
      >
        {["Pt", "Sa", "Ça", "Pe", "Cu", "Ct", "Pz"].map((day) => (
          <Typography
            key={day}
            align="center"
            variant="caption"
            color="text.secondary"
          >
            {day}
          </Typography>
        ))}
        {days.map((day) => {
          const key = localDateString(day);
          const items = taskByDate[key] || [];
          const currentMonth = day.getMonth() === month.getMonth();
          return (
            <Button
              key={key}
              onClick={() => onDateSelect(key)}
              variant={selectedDate === key ? "contained" : "text"}
              sx={{
                minWidth: 0,
                height: 48,
                p: 0.4,
                flexDirection: "column",
                color:
                  selectedDate === key
                    ? "#fff"
                    : currentMonth
                      ? "text.primary"
                      : "text.disabled",
                borderRadius: 2,
                bgcolor: selectedDate === key ? "#4f46e5" : "transparent",
                "&:hover": { bgcolor: selectedDate === key ? "#3730a3" : "#ede9fe" },
              }}
            >
              <Typography variant="caption" component="span">
                {day.getDate()}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.25, minHeight: 6 }}>
                {items.slice(0, 3).map((task) => (
                  <Box
                    key={task.id}
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      bgcolor:
                        task.priority === "urgent"
                          ? "error.main"
                          : task.priority === "normal"
                            ? "primary.main"
                            : "grey.500",
                    }}
                  />
                ))}
              </Box>
            </Button>
          );
        })}
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Typography variant="caption" color="error.main">
          ● Acil
        </Typography>
        <Typography variant="caption" color="primary.main">
          ● Normal
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ● Düşük
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function TasksPage() {
  const user = useMemo(currentUser, []);
  const isAdmin = user?.role === "admin";
  const [scope, setScope] = useState(isAdmin ? "all" : "mine");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [month, setMonth] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const [selectedDate, setSelectedDate] = useState(localDateString());
  const [filter, setFilter] = useState("selected");
  const [editorTask, setEditorTask] = useState(undefined);
  const [detailTask, setDetailTask] = useState(null);
  const [references, setReferences] = useState({ users: [] });
  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setTasks(await listTasks({ scope, limit: 1000 }));
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Görevler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [scope]);
  const loadReferences = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setReferences({ users: (await listUsers()) || [] });
    } catch {}
  }, [isAdmin]);
  useEffect(() => {
    loadTasks();
  }, [loadTasks]);
  useEffect(() => {
    loadReferences();
  }, [loadReferences]);
  const list = useMemo(
    () =>
      tasks.filter((task) => {
        const date = taskDate(task);
        if (filter === "selected") return date === selectedDate;
        if (filter === "today")
          return date === localDateString() && task.status === "open";
        if (filter === "overdue")
          return task.status === "open" && date && date < localDateString();
        if (filter === "open") return task.status === "open";
        if (filter === "completed") return task.status === "completed";
        if (filter === "cancelled") return task.status === "cancelled";
        return true;
      }),
    [tasks, filter, selectedDate],
  );
  const openCount = tasks.filter((task) => task.status === "open").length;
  const overdueCount = tasks.filter(
    (task) =>
      task.status === "open" &&
      taskDate(task) &&
      taskDate(task) < localDateString(),
  ).length;
  async function quickComplete(task, event) {
    event.stopPropagation();
    try {
      await completeTask(task.id);
      await loadTasks();
    } catch (err) {
      alert(err?.response?.data?.error?.message || "Görev tamamlanamadı.");
    }
  }
  return (
    <Box sx={{ maxWidth: 1540, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 2.5,
          flexWrap: "wrap",
          p: { xs: 2, sm: 2.5 },
          borderRadius: 4,
          bgcolor: "#f5f3ff",
          border: "1px solid #c7d2fe",
          boxShadow: "0 12px 30px rgba(79, 70, 229, 0.10)",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Görevler
          </Typography>
          <Typography color="text.secondary">
            İşleri atayın, takip edin ve tamamlanma kaydını tek yerde tutun.
          </Typography>
        </Box>
        {isAdmin ? (
          <Button variant="contained" onClick={() => setEditorTask(null)} sx={{ px: 2.25, py: 1, borderRadius: 2, textTransform: "none", fontWeight: 800, bgcolor: "#4f46e5", boxShadow: "none", "&:hover": { bgcolor: "#3730a3", boxShadow: "none" } }}>
            + Yeni Görev
          </Button>
        ) : null}
      </Box>
      {isAdmin ? (
        <ToggleButtonGroup
          value={scope}
          exclusive
          size="small"
          onChange={(_, value) => value && setScope(value)}
          sx={{ mb: 2.5, p: 0.5, borderRadius: 3, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", "& .MuiToggleButton-root": { border: 0, borderRadius: "10px !important", textTransform: "none", fontWeight: 800, px: 1.5 }, "& .Mui-selected": { bgcolor: "#4f46e5 !important", color: "#fff !important" } }}
        >
          <ToggleButton value="all">Tüm Görevler</ToggleButton>
          <ToggleButton value="mine">Görevlerim</ToggleButton>
        </ToggleButtonGroup>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <MonthCalendar
            month={month}
            tasks={tasks.filter((task) => task.status === "open")}
            selectedDate={selectedDate}
            onMonthChange={setMonth}
            onDateSelect={(date) => {
              setSelectedDate(date);
              setFilter("selected");
            }}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{ borderRadius: 4, overflow: "hidden", bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.10)", "& .MuiList-root": { bgcolor: "#fff" }, "& .MuiListItemButton-root:hover": { bgcolor: "#fafaff" } }}
          >
            <Box sx={{ p: 2, pb: 1 }}>
              <Stack
                direction={{ xs: "column", lg: "row" }}
                justifyContent="space-between"
                gap={1}
              >
                <Box>
                  <Typography fontWeight={800}>Görev listesi</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {openCount} açık görev{" "}
                    {overdueCount ? `· ${overdueCount} gecikmiş` : ""}
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {[
                    ["selected", "Seçili Gün"],
                    ["today", "Bugün"],
                    ["open", "Açık"],
                    ["overdue", "Geciken"],
                    ["completed", "Tamamlanan"],
                    ["cancelled", "İptal"],
                  ].map(([value, label]) => (
                    <Button
                      key={value}
                      size="small"
                      variant={filter === value ? "contained" : "text"}
                      onClick={() => setFilter(value)}
                      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, ...(filter === value ? { bgcolor: "#4f46e5", boxShadow: "none", "&:hover": { bgcolor: "#3730a3", boxShadow: "none" } } : { color: "#4f46e5" }) }}
                    >
                      {label}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            </Box>
            <Divider />
            {loading ? (
              <Box sx={{ py: 7, textAlign: "center" }}>
                <CircularProgress size={28} />
              </Box>
            ) : list.length === 0 ? (
              <Box sx={{ py: 7, textAlign: "center" }}>
                <Typography color="text.secondary">
                  Bu görünümde görev yok.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {list.map((task) => (
                  <ListItem
                    key={task.id}
                    disablePadding
                    divider
                    secondaryAction={
                      task.status === "open" ? (
                        <Checkbox
                          edge="end"
                          color="success"
                          onClick={(event) => quickComplete(task, event)}
                          inputProps={{ "aria-label": "Tamamlandı" }}
                        />
                      ) : null
                    }
                  >
                    <ListItemButton
                      onClick={() => setDetailTask(task)}
                      sx={{
                        py: 1.25,
                        px: 2,
                        opacity: task.status === "cancelled" ? 0.55 : 1,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography
                              sx={{
                                textDecoration:
                                  task.status === "completed"
                                    ? "line-through"
                                    : "none",
                                fontWeight: 700,
                              }}
                            >
                              {task.title}
                            </Typography>
                            <Chip
                              label={PRIORITY_LABELS[task.priority]}
                              size="small"
                              color={PRIORITY_COLORS[task.priority]}
                            />
                          </Stack>
                        }
                        secondary={`${task.assigned_to_name} · ${formatDateTime(task.due_at)}${task.customer_name ? ` · ${task.customer_name}` : ""}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
      <TaskEditor
        open={editorTask !== undefined}
        task={editorTask}
        {...references}
        onClose={() => setEditorTask(undefined)}
        onSaved={loadTasks}
      />
      <TaskDetails
        task={detailTask}
        isAdmin={isAdmin}
        onClose={() => setDetailTask(null)}
        onChanged={loadTasks}
        onEdit={(task) => {
          setDetailTask(null);
          setEditorTask(task);
        }}
      />
    </Box>
  );
}
